import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { API } from "../lib/api";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

const POLAND_CENTER = [52.0, 19.4];
const POLL_INTERVAL_MS = 5000;

/** Custom car-shaped SVG marker rotated by heading. */
function carIcon(heading = 0, isMoving = true) {
  const color = isMoving ? "#E52C34" : "#71717A";
  const svg = `
    <div style="transform: rotate(${heading}deg); transform-origin: center;">
      <svg width="42" height="42" viewBox="0 0 42 42" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="s" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" flood-opacity="0.45"/>
          </filter>
        </defs>
        <!-- pointer triangle for heading (points up = north before rotation) -->
        <path d="M 21 2 L 27 12 L 15 12 Z" fill="${color}" opacity="0.9" />
        <!-- car body -->
        <g filter="url(#s)">
          <rect x="10" y="13" width="22" height="24" rx="4" fill="${color}" stroke="#18181b" stroke-width="1.5"/>
          <!-- roof / windshield -->
          <rect x="13" y="16" width="16" height="9" rx="2" fill="#ffffff" opacity="0.92" stroke="#18181b" stroke-width="0.8"/>
          <line x1="21" y1="16" x2="21" y2="25" stroke="#18181b" stroke-width="0.7" opacity="0.5"/>
          <!-- side stripe -->
          <rect x="10" y="27" width="22" height="2.5" fill="#18181b" opacity="0.55"/>
          <!-- wheels -->
          <rect x="8" y="18" width="3" height="6" rx="1" fill="#18181b"/>
          <rect x="31" y="18" width="3" height="6" rx="1" fill="#18181b"/>
          <rect x="8" y="28" width="3" height="6" rx="1" fill="#18181b"/>
          <rect x="31" y="28" width="3" height="6" rx="1" fill="#18181b"/>
          <!-- headlights -->
          <rect x="13" y="13.5" width="3" height="1.5" rx="0.5" fill="#FBBF24"/>
          <rect x="26" y="13.5" width="3" height="1.5" rx="0.5" fill="#FBBF24"/>
        </g>
      </svg>
    </div>
  `;
  return L.divIcon({
    html: svg,
    className: "vehicle-car-icon",
    iconSize: [42, 42],
    iconAnchor: [21, 21],
    popupAnchor: [0, -16],
  });
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 5) return "teraz";
  if (seconds < 60) return `${seconds}s temu`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} min temu`;
  return d.toLocaleString("pl-PL");
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [points.length]); // only when count changes — avoid jank on every refresh
  return null;
}

export default function FleetMap() {
  const [locations, setLocations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const timerRef = useRef(null);

  const fetchLocations = async () => {
    try {
      const [locs, alertList] = await Promise.all([
        axios.get(`${API}/fleet/locations/all`).then((r) => r.data),
        axios.get(`${API}/fleet/alerts/active`).then((r) => r.data),
      ]);
      setLocations(locs);
      setAlerts(alertList);
      setLastUpdate(new Date());
    } catch (e) {
      // silent
    }
  };

  const fetchHistory = async (id) => {
    try {
      const res = await axios.get(`${API}/fleet/${id}/location/history`);
      setHistory(res.data.points || []);
    } catch (e) {
      setHistory([]);
    }
  };

  useEffect(() => {
    fetchLocations();
    timerRef.current = setInterval(fetchLocations, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (selectedId) fetchHistory(selectedId);
    else setHistory([]);
  }, [selectedId, locations.length]);

  const selected = locations.find((l) => l.vehicle_id === selectedId);

  return (
    <div className="border border-zinc-900 bg-white" data-testid="fleet-map">
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50">
        <div>
          <div className="font-label mb-1">// Mapa GPS · podgląd na żywo</div>
          <h2 className="font-heading font-black text-xl uppercase tracking-tight">
            Pozycje pojazdów
          </h2>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            LIVE · {locations.length} {locations.length === 1 ? "pojazd" : "pojazdów"}
          </span>
          <span data-testid="last-update">
            Aktualizacja: {lastUpdate ? lastUpdate.toLocaleTimeString("pl-PL") : "—"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">
        {/* Map */}
        <div className="relative" style={{ height: 480 }}>
          <MapContainer
            center={POLAND_CENTER}
            zoom={6}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
            data-testid="map-container"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={locations} />
            {history.length > 1 && (
              <Polyline
                positions={history.map((p) => [p.lat, p.lng])}
                pathOptions={{ color: "#E52C34", weight: 3, opacity: 0.7, dashArray: "6 6" }}
              />
            )}
            {selected && selected.geofence && (
              <Circle
                center={[selected.geofence.lat, selected.geofence.lng]}
                radius={selected.geofence.radius_km * 1000}
                pathOptions={{
                  color: "#E52C34",
                  weight: 2,
                  fillColor: "#E52C34",
                  fillOpacity: 0.06,
                  dashArray: "4 4",
                }}
              />
            )}
            {locations.map((loc) => (
              <Marker
                key={loc.vehicle_id}
                position={[loc.lat, loc.lng]}
                icon={carIcon(loc.heading || 0, (loc.speed_kmh || 0) > 5)}
                eventHandlers={{ click: () => setSelectedId(loc.vehicle_id) }}
              >
                <Popup>
                  <div className="space-y-1 text-sm">
                    <div className="font-bold uppercase tracking-tight">
                      {loc.brand} {loc.model}
                    </div>
                    <div className="font-mono text-xs">{loc.registration}</div>
                    {loc.driver_name && (
                      <div className="text-xs text-brand">
                        Kierowca: <b>{loc.driver_name}</b>
                      </div>
                    )}
                    <div className="pt-1 border-t border-zinc-200 text-xs">
                      Prędkość: <b>{Math.round(loc.speed_kmh || 0)} km/h</b>
                    </div>
                    <div className="text-xs">
                      Kurs: <b>{Math.round(loc.heading || 0)}°</b>
                    </div>
                    {loc.total_km != null && (
                      <div className="text-xs">
                        Przebieg GPS: <b>{loc.total_km.toFixed(1)} km</b>
                      </div>
                    )}
                    <div className="text-xs text-zinc-500">
                      Źródło: {loc.source}
                    </div>
                    <div className="text-xs text-zinc-500">
                      Aktualizacja: {timeAgo(loc.updated_at)}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Side panel — vehicle list */}
        <aside
          className="border-t lg:border-t-0 lg:border-l border-zinc-200 max-h-[480px] overflow-y-auto"
          data-testid="fleet-map-list"
        >
          {locations.length === 0 && (
            <div className="p-6 text-center text-sm text-zinc-500">
              Brak pozycji GPS. Dodaj pojazd, by zobaczyć go na mapie.
            </div>
          )}
          {locations.map((loc) => {
            const isSelected = loc.vehicle_id === selectedId;
            const isMoving = (loc.speed_kmh || 0) > 5;
            return (
              <button
                key={loc.vehicle_id}
                onClick={() =>
                  setSelectedId(isSelected ? null : loc.vehicle_id)
                }
                data-testid={`fleet-map-item-${loc.vehicle_id}`}
                className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                  isSelected ? "bg-red-50 border-l-4 border-l-brand" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-bold text-sm">
                    {loc.brand} {loc.model}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase ${
                      isMoving ? "text-emerald-600" : "text-zinc-400"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        isMoving ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"
                      }`}
                    />
                    {isMoving ? "Jedzie" : "Postój"}
                  </span>
                </div>
                <div className="font-mono text-xs text-zinc-500 mb-1">
                  {loc.registration || "—"}
                </div>
                {loc.driver_name && (
                  <div className="text-[11px] text-brand mb-1">
                    {loc.driver_name}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono">
                    {Math.round(loc.speed_kmh || 0)} km/h
                  </span>
                  <span className="text-zinc-400">{timeAgo(loc.updated_at)}</span>
                </div>
                {loc.total_km != null && (
                  <div className="text-[10px] text-zinc-400 font-mono mt-0.5">
                    GPS: {loc.total_km.toFixed(1)} km
                  </div>
                )}
              </button>
            );
          })}
        </aside>
      </div>

      <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-[11px] font-mono text-zinc-500 flex flex-wrap items-center gap-4">
        <span>// Mapa: OpenStreetMap · Polling co 5s</span>
        <span>// Źródło danych: symulator (gotowy do podpięcia zewn. providera GPS via POST /api/fleet/&#123;id&#125;/location)</span>
      </div>
    </div>
  );
}
